import {
  AdditiveBlending,
  BackSide,
  CircleGeometry,
  DoubleSide,
  Color,
  CylinderGeometry,
  ExtrudeGeometry,
  InstancedMesh,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  Path,
  PlaneGeometry,
  PointLight,
  Shape,
} from 'three';
import { Layer } from './Layer.js';
import { stoneTexture } from '../geometry/textures.js';

/**
 * The building the stair is inside: a cylindrical shaft 232 m deep, ringed
 * storey by storey with arched openings. The stair rakes past them, so the
 * windows pass the traveller at an angle and the light swings across the
 * treads as the city turns.
 *
 * The openings are real holes in the masonry; behind each one sits a pane of
 * sky whose colour is sampled from the palette at that depth — pale and cold
 * near the top of the shaft, lamp-coloured by the time you are down among
 * the old men.
 */
export class Shaft extends Layer {
  static id = 'shaft';
  static order = 20;

  async build() {
    const { settings, palette } = this.ctx;
    const s = settings.stair;
    this.s = s;

    const { bays, storeys } = settings.shaft;
    // The arcade covers the whole shaft, from a little above the top landing
    // down to the floor of the square: there is nowhere on the stair that is
    // not beside a window.
    this.top = this.ctx.helix.topY + 5;
    this.bottom = this.ctx.helix.topY - s.height - 1.2;
    const storeyHeight = (this.top - this.bottom) / storeys;
    this.bays = bays;
    this.storeys = storeys;
    this.storeyHeight = storeyHeight;

    const R = s.wallRadius;
    const chord = 2 * R * Math.sin(Math.PI / bays);
    const apothem = R * Math.cos(Math.PI / bays);
    this.apothem = apothem;

    // ExtrudeGeometry gives UVs in model units, so one repeat is one metre:
    // the coursing comes out at the size of a real block.
    const bump = stoneTexture(512);
    bump.repeat.set(0.55, 0.55);

    this.wallMat = new MeshStandardMaterial({
      color: palette.get('stoneDeep').clone(),
      roughness: 0.94,
      metalness: 0.0,
      bumpMap: bump,
      bumpScale: 0.6,
      roughnessMap: bump,
    });

    // World positions of every arch. Shared on the context because two other
    // layers need them — the light pool here, and the telescopes, which have
    // to be able to find something to look out of.
    this.openings = [];
    this.ctx.openings = this.openings;
    this._buildArcade(chord, apothem, storeyHeight, bays, storeys);
    this._buildPanes(chord, apothem, storeyHeight, bays, storeys);
    this._buildVault(R, s);
    this._buildWindowLights();
  }

  /** One bay: a flat panel of masonry with an arched opening cut through it. */
  _bayGeometry(width, height) {
    const w = width / 2;
    const shape = new Shape();
    shape.moveTo(-w, 0);
    shape.lineTo(w, 0);
    shape.lineTo(w, height);
    shape.lineTo(-w, height);
    shape.closePath();

    const ow = width * 0.58; // opening width — an arcade, not a row of slits
    const sill = height * 0.13;
    const spring = sill + ow * 1.05;
    const hw = ow / 2;

    const hole = new Path();
    hole.moveTo(-hw, sill);
    hole.lineTo(hw, sill);
    hole.lineTo(hw, spring);
    // A slightly pointed arch: two arcs struck from the opposite springing.
    hole.absarc(0, spring, hw, 0, Math.PI, false);
    hole.lineTo(-hw, sill);
    hole.closePath();
    shape.holes.push(hole);

    const geo = new ExtrudeGeometry(shape, {
      depth: 0.95,
      bevelEnabled: true,
      bevelThickness: 0.07,
      bevelSize: 0.09,
      bevelSegments: 2,
      curveSegments: 14,
    });
    geo.translate(0, 0, -0.95);
    geo.computeVertexNormals();
    return geo;
  }

  _buildArcade(chord, apothem, storeyHeight, bays, storeys) {
    const geo = this._bayGeometry(chord * 1.004, storeyHeight * 1.004);
    const total = bays * storeys;
    const mesh = new InstancedMesh(geo, this.wallMat, total);
    mesh.frustumCulled = false;

    const dummy = new Object3D();
    let i = 0;
    for (let sIdx = 0; sIdx < storeys; sIdx++) {
      const twist = 0; // storeys line up: a wall, not a stack of boxes
      const y = this.top - (sIdx + 1) * storeyHeight;
      for (let b = 0; b < bays; b++) {
        const a = (b / bays) * Math.PI * 2 + twist;
        dummy.position.set(Math.cos(a) * apothem, y, Math.sin(a) * apothem);
        dummy.rotation.set(0, -a + Math.PI / 2, 0);
        dummy.scale.set(1, 1, 1);
        dummy.updateMatrix();
        mesh.setMatrixAt(i++, dummy.matrix);

        // Remember where the light will come in.
        const r = apothem - 0.6;
        this.openings.push({
          x: Math.cos(a) * r,
          y: y + storeyHeight * 0.42,
          z: Math.sin(a) * r,
          depth: sIdx / (storeys - 1),
        });
      }
    }
    mesh.instanceMatrix.needsUpdate = true;
    this.arcade = mesh;
    this.group.add(mesh);
  }

  /**
   * Every opening carries a veil of light across it — the glare of outside
   * air, hanging in the hole. It is additive and thin, so the sky, the plain
   * and the dreamed city all still show through: the window is a window, not
   * a lamp pretending to be one.
   */
  _buildPanes(chord, apothem, storeyHeight, bays, storeys) {
    const geo = new PlaneGeometry(chord * 0.5, storeyHeight * 0.58);
    this.paneMat = new MeshBasicMaterial({
      toneMapped: true,
      fog: false,
      transparent: true,
      opacity: 0.26,
      side: DoubleSide,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    const total = bays * storeys;
    const mesh = new InstancedMesh(geo, this.paneMat, total);
    mesh.frustumCulled = false;

    const dummy = new Object3D();
    const col = new Color();
    this._paneBase = [];
    let i = 0;
    for (let sIdx = 0; sIdx < storeys; sIdx++) {
      const depth = sIdx / (storeys - 1);
      const y = this.top - (sIdx + 1) * storeyHeight + storeyHeight * 0.42;
      for (let b = 0; b < bays; b++) {
        const a = (b / bays) * Math.PI * 2;
        const r = apothem + 0.15;
        dummy.position.set(Math.cos(a) * r, y, Math.sin(a) * r);
        dummy.rotation.set(0, -a + Math.PI / 2, 0);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
        // Remembered so the colour can be re-aged every frame.
        this._paneBase.push(depth);
        mesh.setColorAt(i, col.setRGB(1, 1, 1));
        i++;
      }
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    this.panes = mesh;
    this.group.add(mesh);
    this._paneColor = new Color();
    this._paneWarm = new Color();
  }

  /**
   * Three lights that stand in for the sun coming through whichever openings
   * the traveller happens to be beside. Re-aimed every frame, so the stair is
   * always raked by light from a real hole in a real wall rather than lit by
   * an ambient term that belongs to nowhere.
   */
  _buildWindowLights() {
    this.windowLights = [0, 1, 2].map(() => {
      const l = new PointLight(0xffffff, 0, 62, 2);
      this.group.add(l);
      return l;
    });
    this._lightColor = new Color();
  }

  /** The lantern overhead and the floor of the square underfoot. */
  _buildVault(R, s) {
    const topY = this.ctx.helix.topY;

    // A lantern over the top landing: a drum, open to the sky, which is the
    // last daylight anyone on this stair is going to get.
    const drum = new CylinderGeometry(R, R * 0.82, 20, 48, 1, true);
    drum.translate(0, topY + 15, 0);
    const drumMesh = new Mesh(drum, this.wallMat.clone());
    drumMesh.material.side = BackSide;
    this.drumMat = drumMesh.material;
    this.group.add(drumMesh);

    // The floor of the square, far below.
    const floorY = topY - s.height - 1.2;
    this.floorMat = new MeshStandardMaterial({
      color: this.ctx.palette.get('stoneDeep').clone(),
      roughness: 0.88,
      metalness: 0.03,
    });
    const floor = new Mesh(new CircleGeometry(R + 1, 64), this.floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = floorY;
    this.group.add(floor);
    this.floorY = floorY;

    // The well does not simply end: below the floor the shaft keeps going as
    // a darker, emptier echo, which is what you see when you look down early.
    const below = new CylinderGeometry(s.innerRadius, s.innerRadius * 0.7, 120, 32, 1, true);
    below.translate(0, floorY - 60, 0);
    const belowMesh = new Mesh(below, this.wallMat.clone());
    belowMesh.material.side = BackSide;
    belowMesh.material.color = new Color(0x05070e);
    this.group.add(belowMesh);
  }

  /** What colour is the outside, at this depth, at this hour? */
  _outsideAt(depth, t, target) {
    const { palette } = this.ctx;
    palette.sample('sky', Math.min(1, t * 0.45 + depth * 0.85), target);
    palette.sample('lamp', t, this._paneWarm);
    // Deep down, "outside" stops meaning sky and starts meaning streetlight.
    const warmth = Math.min(0.92, Math.pow(depth, 2.0) * 0.8 + t * 0.2);
    target.lerp(this._paneWarm, warmth);
    const brightness = 1.55 - depth * 0.5 - t * 0.35;
    return target.multiplyScalar(Math.max(0.22, brightness));
  }

  update(dt, state) {
    const { palette } = this.ctx;
    // Between the stone of the stair and the dark of the fascia: the wall
    // has to read as masonry, not as the absence of anything.
    this.wallMat.color.copy(palette.get('stone')).lerp(palette.get('stoneDeep'), 0.45);
    this.drumMat.color.copy(palette.get('stone')).multiplyScalar(0.9);
    this.floorMat.color.copy(palette.get('stoneDeep'));

    if (!this.panes) return;

    // The veil is glare: it belongs to an eye looking at a bright hole from
    // inside a dark shaft. Put that eye to a telescope and it adapts, so the
    // veil has to get out of the way of the view.
    this.paneMat.opacity = 0.26 * (1 - state.iris);
    this.panes.visible = this.paneMat.opacity > 0.004;

    const sky = this._paneColor;
    const total = this.panes.count;
    for (let i = 0; i < total; i++) {
      this._outsideAt(this._paneBase[i], state.t, sky);
      this.panes.setColorAt(i, sky);
    }
    if (this.panes.instanceColor) this.panes.instanceColor.needsUpdate = true;

    this._aimLights(state);
  }

  _aimLights(state) {
    const cam = this.ctx.camera.position;
    // The three nearest openings, found by a single pass: there are only a
    // few hundred and they never move.
    const best = [null, null, null];
    const bestD = [Infinity, Infinity, Infinity];
    for (const o of this.openings) {
      const dx = o.x - cam.x;
      const dy = o.y - cam.y;
      const dz = o.z - cam.z;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 > 2500) continue;
      if (d2 < bestD[0]) {
        bestD[2] = bestD[1];
        best[2] = best[1];
        bestD[1] = bestD[0];
        best[1] = best[0];
        bestD[0] = d2;
        best[0] = o;
      } else if (d2 < bestD[1]) {
        bestD[2] = bestD[1];
        best[2] = best[1];
        bestD[1] = d2;
        best[1] = o;
      } else if (d2 < bestD[2]) {
        bestD[2] = d2;
        best[2] = o;
      }
    }

    this.windowLights.forEach((light, i) => {
      const o = best[i];
      if (!o) {
        light.intensity = 0;
        return;
      }
      light.position.set(o.x, o.y, o.z);
      this._outsideAt(o.depth, state.t, this._lightColor);
      // Normalise the hue away from the brightness: the colour comes from
      // the sky outside, the strength from how deep the opening is.
      const lum = Math.max(
        0.12,
        this._lightColor.r * 0.21 + this._lightColor.g * 0.72 + this._lightColor.b * 0.07,
      );
      light.color.copy(this._lightColor).multiplyScalar(1 / lum);
      const fade = 1 - Math.min(1, Math.sqrt(bestD[i]) / 50);
      light.intensity = (58 - state.t * 26) * fade * fade;
    });
  }

  stats() {
    return {
      bays: this.arcade?.count ?? 0,
      lights: this.windowLights?.filter((l) => l.intensity > 1).length ?? 0,
    };
  }
}
